import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useUrlParams from '../hooks/useUrlParams';
import Layout from '../components/Layout';
import { MemberIndexEntry, MinistryBudget, MinistryMeta, QueryDoc, SecondaryLawRow } from '../types/types';
import {
  getCommitteeList, getMemberSummaries, getMinistries, getMinistryBudget, getParties, getQueries, getSecondaryLaws, getSecondaryLawStats,
} from '../data/cache';
import { useScope } from '../hooks/useScope';
import { shortPartyName } from '../utils/party';
import { photoFor } from '../utils/names';
import { buttonReset, color, font, radius, space, text, Tone, weight } from '../theme';
import DetailHeader from '../components/DetailHeader';
import TabBar from '../components/TabBar';
import { Card, CardHeader, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge } from '../components/Badge';
import Chip, { ChipRow } from '../components/Chip';
import Bar from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import NavIcon from '../layout/NavIcon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import SecondaryLawList from '../components/SecondaryLawList';
import { useBreakpoint } from '../useBreakpoint';
import PageContainer from '../layout/PageContainer';
import DetailHeaderFrame from '../layout/DetailHeaderFrame';
import TwoPane from '../layout/TwoPane';

// ── Helpers ──────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'budget' | 'queries' | 'seclaw';
const QUERIES_PAGE = 30;

// Budget figures are in thousands of ₪
const fmtBudget = (thousands: number): string => {
  if (thousands >= 1_000_000) return `₪${(thousands / 1_000_000).toFixed(1)}B`;
  if (thousands >= 1_000) return `₪${(thousands / 1_000).toFixed(1)}M`;
  return `₪${thousands.toLocaleString('he-IL')}K`;
};

const GRADE_TONE: Record<string, Tone> = { A: 'success', B: 'info', C: 'warning', D: 'danger' };

// Execution against the original budget: on target, close, off, far off (status colors)
const execBarColor = (util: number): string => {
  if (util >= 93 && util <= 102) return '#3f7a16';
  if (util >= 85 && util <= 110) return color.primary;
  if (util >= 75 && util <= 120) return '#9a6a2a';
  return '#B42D2C';
};

// Execution excluding war spending, as a share of the previous year's original budget
// (or this year's when there is none)
const adjustedUtilization = (d: MinistryBudget['years'][string]) => {
  const baseline = d.prevOriginal && d.prevOriginal > 0 ? d.prevOriginal : d.original;
  return baseline > 0 ? Math.round(((d.execution - d.warExecution) / baseline) * 100) : d.utilizationPct;
};

const MINISTRY_DESCRIPTIONS: Record<string, string> = {
  'משרד הביטחון':                            'אחראי על ביטחון המדינה, צבא ההגנה לישראל, ורכש ביטחוני.',
  'משרד האוצר':                              'מנהל את תקציב המדינה, מדיניות כלכלית, מיסוי ורגולציה פיננסית.',
  'משרד החוץ':                               'אחראי על יחסי החוץ של ישראל, שגרירויות ומשלחות דיפלומטיות.',
  'משרד המשפטים':                            'מפקח על מערכת המשפט, חקיקה, ייעוץ משפטי לממשלה ורשם החברות.',
  'משרד הפנים':                              'אחראי על רישום אוכלוסין, מעמד אישי, רשויות מקומיות ומדיניות הגירה.',
  'משרד החינוך':                             'מנהל את מערכת החינוך הציבורית מגיל הגן ועד התיכון.',
  'משרד הבריאות':                            'אחראי על מערכת הבריאות הציבורית ופיקוח על קופות החולים.',
  'משרד הרווחה והביטחון החברתי':            'מספק שירותי רווחה ומנהל את מערכת הקצבאות.',
  'המשרד לביטחון לאומי':                    'אחראי על משטרת ישראל, שב"ס ומדיניות הביטחון הפנימי.',
  'משרד התחבורה והבטיחות בדרכים':           'מפתח תשתיות תחבורה ומפקח על כבישים ותחבורה ציבורית.',
  'משרד האנרגיה והתשתיות':                  'מתכנן מדיניות אנרגיה ומפקח על גז טבעי וחשמל.',
  'משרד הכלכלה והתעשייה':                   'מקדם עסקים, תעשייה, מסחר חוץ ותעסוקה.',
  'משרד התקשורת':                            'מסדיר את שוק התקשורת, רדיו, טלוויזיה ואינטרנט.',
  'משרד החקלאות ופיתוח הכפר':               'תומך בענף החקלאות ומפקח על ביטחון מזון.',
  'המשרד להגנת הסביבה':                     'שומר על איכות הסביבה ומנהל שמורות טבע.',
  'משרד התיירות':                            'מקדם את ישראל כיעד תיירותי ומפתח תשתיות תיירות.',
  'משרד הבינוי והשיכון':                    'אחראי על מדיניות הדיור ותכנון בנייה.',
  'משרד המדע והטכנולוגיה':                  'מקדם מחקר אקדמי וחדשנות טכנולוגית.',
  'משרד התרבות והספורט':                    'תומך בתרבות, אמנות, ספורט ופנאי.',
  'משרד העלייה והקליטה':                    'מסייע לעולים חדשים בקליטתם בישראל.',
  'משרד ראש הממשלה':                         'מרכז את פעילות הממשלה ומתאם בין המשרדים.',
  'משרד העבודה':                             'אחראי על שוק העבודה, זכויות עובדים ובטיחות בעבודה.',
  'המשרד לשירותי דת':                       'מנהל שירותי דת, מועצות דתיות ורישום נישואין.',
  'משרד החדשנות, המדע והטכנולוגיה':         'מקדם מחקר אקדמי, חדשנות וסטארט-אפים ישראליים.',
};

const formatShortDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: '2-digit' }) : '';

// ── Component ────────────────────────────────────────────────────────────────

const MinistryDetailPage = () => {
  const { ministryId } = useParams<{ ministryId: string }>();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const [searchParams, setSearchParams] = useUrlParams(); // keeps the caller's `from` across tab switches

  const [ministry, setMinistry] = useState<MinistryMeta | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [budget, setBudget] = useState<MinistryBudget | null>(null);
  const [minister, setMinister] = useState<{ member: MemberIndexEntry; partyName: string; role: string } | null>(null);
  const [queries, setQueries] = useState<QueryDoc[] | null>(null);
  const [queryMembers, setQueryMembers] = useState<Record<string, MemberIndexEntry>>({});
  // Secondary legislation the ministry issued (null until read), the Knesset the list
  // describes, and committee names for the rows' committee links
  const [slRows, setSlRows] = useState<SecondaryLawRow[] | null>(null);
  const [slError, setSlError] = useState(false);
  const [slReload, setSlReload] = useState(0);
  const [slKnesset, setSlKnesset] = useState<number | null>(null);
  const [committeeNames, setCommitteeNames] = useState<Record<string, string>>({});
  const scope = useScope();

  const requested = searchParams.get('tab') as Tab | null;
  const activeTab: Tab = requested === 'budget' || requested === 'queries' || requested === 'seclaw' ? requested : 'overview';
  const setActiveTab = (tab: Tab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (tab === 'overview') next.delete('tab'); else next.set('tab', tab);
    if (tab !== 'seclaw') next.delete('kind'); // the secondary-law list's filter
    return next;
  }, { replace: true });
  const selectedYearParam = searchParams.get('year') || '';
  const queriesShown = parseInt(searchParams.get('n') || '', 10) || QUERIES_PAGE;
  const setParam = (key: string, value: string | null) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });

  // Ministry from meta/ministries, then its budget and minister (all cached between pages)
  useEffect(() => {
    if (!ministryId) return;
    let cancelled = false;
    setStatus('loading');
    getMinistries().then(byId => {
      if (cancelled) return;
      const m = byId[ministryId];
      if (!m) { setStatus('notFound'); return; }
      const mm = { ...m, id: ministryId, category: m.CategoryName || m.categoryName || m.category || '' };
      setMinistry(mm);
      setStatus('ready');

      getMinistryBudget(mm).then(b => { if (!cancelled) setBudget(b); }).catch(() => { /* ok — budget is optional */ });

      if (mm.currentMinisterId) {
        const mid = mm.currentMinisterId;
        Promise.all([getMemberSummaries([mid]), getParties()]).then(([members, parties]) => {
          if (cancelled || !members[mid]) return;
          const member = members[mid];
          const party = member.partyId ? parties.find(p => p.id === member.partyId) : undefined;
          const govRoles = (member.roles || []).filter(r => r.category === 'government');
          setMinister({ member, partyName: party ? shortPartyName(party.name || '') : '', role: govRoles.length > 0 ? govRoles[0].title : member.role || '' });
        }).catch(() => { /* ok — minister card is optional */ });
      }
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [ministryId, reloadKey]);

  // The ministry's queries come from the shared query list, loaded with the tab
  useEffect(() => {
    if (activeTab !== 'queries' || queries || !ministryId) return;
    let cancelled = false;
    const numericId = parseInt(ministryId, 10);
    getQueries().then(list => {
      if (cancelled) return;
      const mine = list.filter(q => q.govMinistryId === numericId);
      setQueries(mine);
      getMemberSummaries(mine.map(q => q.memberId || '')).then(m => { if (!cancelled) setQueryMembers(m); }).catch(() => { /* ok */ });
    }).catch(() => { if (!cancelled) setQueries([]); });
    return () => { cancelled = true; };
  }, [activeTab, queries, ministryId]);

  // The ministry's secondary legislation, loaded with its tab: the current Knesset's items
  // whose issuers include a ministry of the same category (KNS_GovMinistry.CategoryID is the
  // same in every government). The totals come first, so a ministry with none skips the list.
  const categoryId = ministry && typeof ministry.categoryId === 'number' ? ministry.categoryId : null;
  useEffect(() => {
    if (activeTab !== 'seclaw' || categoryId === null || slRows !== null) return;
    let cancelled = false;
    setSlError(false);
    getSecondaryLawStats()
      .then(stats => {
        if (cancelled) return null;
        setSlKnesset(stats && stats.knessetNumber ? stats.knessetNumber : null);
        if (stats && !stats.byMinistryCategory[String(categoryId)]) return [];
        return getSecondaryLaws().then(list => list.filter(r => (r.ministryCategoryIds || []).indexOf(categoryId) !== -1));
      })
      .then(rows => { if (!cancelled && rows) setSlRows(rows); })
      .catch(() => { if (!cancelled) setSlError(true); });
    getCommitteeList().then(list => {
      if (cancelled) return;
      const names: Record<string, string> = {};
      for (const c of list) names[c.id] = c.name;
      setCommitteeNames(names);
    }).catch(() => { /* ok — rows show the committee only when its name is known */ });
    return () => { cancelled = true; };
  }, [activeTab, categoryId, slRows, slReload]);

  // The five MKs who asked this ministry the most
  const topQuestioners = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const q of queries || []) if (q.memberId) counts[q.memberId] = (counts[q.memberId] || 0) + 1;
    return Object.keys(counts).map(id => ({ id, count: counts[id] })).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [queries]);

  /* ── Loading, error, not found ── */

  if (status === 'loading') {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Skeleton width={80} height={20} /><Skeleton width="70%" height={24} /><SkeletonText lines={2} /></div></Card>
          <SkeletonList rows={3} avatar />
        </div>
      </Layout>
    );
  }
  if (status === 'error') {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (status === 'notFound' || !ministry) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="document" title="המשרד לא נמצא" body="ייתכן שהקישור שגוי או שהמשרד אינו פעיל." action={<Button onClick={() => navigate('/government?tab=ministries', { replace: true })}>לכל המשרדים</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const desc = MINISTRY_DESCRIPTIONS[ministry.name];
  const queryCount = ministry.queryCount || 0;
  const answeredCount = ministry.answeredCount || 0;
  const budgetYears = budget ? Object.keys(budget.years).sort() : [];
  const hasBudget = budgetYears.length > 0;
  const from = ministry.name;

  const tabBar = (
    <TabBar
      label="לשוניות המשרד"
      tabs={[
        { key: 'overview', label: 'סקירה' },
        { key: 'budget', label: 'תקציב' },
        // Four segments fit a phone only without counts (the overview card has the query total)
        { key: 'queries', label: 'שאילתות' },
        { key: 'seclaw', label: 'חקיקת משנה' },
      ]}
      active={activeTab}
      onChange={setActiveTab}
      // nowrap (inherited by the segments): "חקיקת משנה" stays on one line on a phone
      style={isDesktop ? { maxWidth: 480, whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap' }}
    />
  );

  return (
    <Layout width="full" breadcrumbs={[{ label: 'משרדים', to: '/government?tab=ministries' }, { label: ministry.name }]}>
      <PageContainer>
      {/* Desktop: the header card is a sticky side panel; the tabs sit above the main column so
          the panel lines up with the first card. Phone/tablet: header, then tabs and content. */}
      <TwoPane
        sideWidth={320}
        mainHeader={isDesktop ? <div style={{ marginBottom: space.lg }}>{tabBar}</div> : undefined}
        side={<DetailHeaderFrame>
      <DetailHeader fallback="/government?tab=ministries" fallbackLabel="הממשלה">
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          {ministry.category && <span><Badge>{ministry.category}</Badge></span>}
          <h1 style={text.entityTitle}>{ministry.name}</h1>
          {desc && <p style={{ margin: 0, fontSize: font.label, lineHeight: 1.6, color: color.textSecondary }}>{desc}</p>}
        </div>
      </DetailHeader>
        </DetailHeaderFrame>}
      >

      <div style={{ padding: isDesktop ? 0 : space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {!isDesktop && tabBar}

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && (
          <>
            <Card padding={0}>
              {ministry.currentMinisterId
                ? (
                  <ListRow
                    divider={false}
                    to={`/members/${ministry.currentMinisterId}`}
                    state={{ from }}
                    leading={<Avatar name={minister?.member.name || '?'} src={minister ? photoFor(minister.member, 52) : undefined} size={52} />}
                    title={minister?.member.name || '…'}
                    subtitle={
                      <span style={{ display: 'flex', flexDirection: 'column', gap: space.xxs }}>
                        <span>{[minister?.role, minister?.partyName].filter(Boolean).join(' · ')}</span>
                        {(ministry.ministerTenureDays != null || (ministry.ministerBillCount || 0) > 0) && (
                          <span style={{ fontSize: font.caption }}>
                            {[
                              ministry.ministerTenureDays != null ? `בתפקיד ${ministry.ministerTenureDays.toLocaleString('he-IL')} ימים` : null,
                              (ministry.ministerBillCount || 0) > 0 ? `${ministry.ministerBillCount} הצעות חוק` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                    }
                  />
                )
                : <div style={{ padding: space.lg, fontSize: font.label, color: color.textSecondary }}>אין שר/ה ממונה</div>}
              {ministry.oversightCommitteeId && (
                <ListRow
                  to={`/committees/${ministry.oversightCommitteeId}`}
                  state={{ from }}
                  leading={<span style={{ width: 52, height: 52, borderRadius: '50%', background: color.primarySoft, color: color.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><NavIcon name="committees" size={22} /></span>}
                  title={ministry.oversightCommitteeName || 'ועדת הפיקוח'}
                  subtitle="ועדת פיקוח"
                />
              )}
            </Card>

            {queryCount > 0 && (
              <Card>
                <CardHeader
                  title="שאילתות פרלמנטריות"
                  action={<button type="button" onClick={() => setActiveTab('queries')} style={{ ...buttonReset, color: color.primary, minHeight: 32 }}>לכל השאילתות</button>}
                />
                <StatTileRow columns={ministry.avgResponseDays != null ? 4 : 3}>
                  <StatTile variant="flat" value={queryCount} label="סה״כ" />
                  <StatTile variant="flat" value={answeredCount} label="נענו" />
                  <StatTile variant="flat" value={queryCount - answeredCount} label="ממתינות" />
                  {ministry.avgResponseDays != null && <StatTile variant="flat" value={ministry.avgResponseDays} label="ימי מענה" />}
                </StatTileRow>
                <Bar
                  value={answeredCount}
                  max={queryCount}
                  height={8}
                  style={{ marginTop: space.md }}
                  color={answeredCount / queryCount > 0.7 ? '#3f7a16' : answeredCount / queryCount > 0.4 ? color.primary : '#9a6a2a'}
                  label={`${Math.round((answeredCount / queryCount) * 100)}% מהשאילתות נענו`}
                />
                <div style={{ fontSize: font.small, color: color.textSecondary, marginTop: space.xs }}>{Math.round((answeredCount / queryCount) * 100)}% מהשאילתות נענו</div>
              </Card>
            )}

            {hasBudget && budget && (() => {
              const lastYear = budgetYears[budgetYears.length - 1];
              const latest = budget.years[lastYear];
              const util = adjustedUtilization(latest);
              const grade = budget.grade;
              return (
                <Card>
                  <CardHeader
                    title={`ביצוע תקציב ${lastYear}`}
                    action={grade ? <Badge tone={GRADE_TONE[grade.letter] || 'neutral'} style={{ fontSize: font.label, padding: '2px 10px' }}>ציון {grade.letter}</Badge> : undefined}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
                    <Bar value={Math.min(util, 100)} height={8} color={execBarColor(util)} style={{ flex: 1 }} label={`ניצול ${util}%`} />
                    <span style={{ fontSize: font.body, fontWeight: weight.bold, flexShrink: 0 }}>{util}%</span>
                  </div>
                  <div style={{ fontSize: font.small, color: color.textSecondary, marginTop: space.sm }}>
                    {fmtBudget(latest.original)} תקציב מקורי · {fmtBudget(latest.execution)} בוצע
                    {latest.warExecution > 0 && ` · ${fmtBudget(latest.warExecution)} מלחמה`}
                  </div>
                  <Button variant="ghost" onClick={() => setActiveTab('budget')} style={{ marginTop: space.xs, paddingInlineStart: 0 }}>לנתוני התקציב המלאים</Button>
                </Card>
              );
            })()}
          </>
        )}

        {/* ═══ BUDGET TAB ═══ */}
        {activeTab === 'budget' && (
          !hasBudget || !budget
            ? <Card padding={0}><EmptyState icon="document" title="אין נתוני תקציב" body="לא נמצאו נתוני תקציב עבור משרד זה." /></Card>
            : (() => {
              const activeYear = selectedYearParam && budgetYears.includes(selectedYearParam) ? selectedYearParam : budgetYears[budgetYears.length - 1];
              const d = budget.years[activeYear];
              const grade = budget.grade;
              const maxVal = Math.max(d.original, d.revised, d.execution);
              const util = adjustedUtilization(d);
              const warOfExecPct = d.execution > 0 ? Math.round((d.warExecution / d.execution) * 100) : 0;
              const maxExec = Math.max(...budgetYears.map(yr => budget.years[yr].execution));
              return (
                <>
                  <ChipRow>
                    {budgetYears.map(yr => (
                      <Chip key={yr} selected={yr === activeYear} onClick={() => setParam('year', yr)}>{yr}</Chip>
                    ))}
                  </ChipRow>

                  <Card>
                    <CardHeader
                      title={activeYear}
                      action={d.budgetGrowthPct !== null
                        ? <Badge tone={d.budgetGrowthPct > 0 ? 'success' : 'danger'}>{d.budgetGrowthPct > 0 ? '+' : ''}{d.budgetGrowthPct}% משנה קודמת</Badge>
                        : undefined}
                    />
                    {[
                      { label: 'תקציב מקורי', val: d.original, barColor: color.primary },
                      { label: 'תקציב על"ש', val: d.revised, barColor: color.primary },
                      { label: 'ביצוע בפועל', val: d.execution, barColor: execBarColor(util) },
                    ].map((row, idx) => (
                      <div key={row.label} style={{ marginBottom: idx < 2 ? space.md : 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: space.xs }}>
                          <span style={{ fontSize: font.small, color: color.textSecondary }}>{row.label}</span>
                          <span style={{ fontSize: font.label, fontWeight: weight.bold }}>{fmtBudget(row.val)}</span>
                        </div>
                        <Bar value={row.val} max={maxVal} height={10} color={row.barColor} />
                        {idx === 2 && d.warExecution > 0 && (
                          <div style={{ fontSize: font.caption, color: color.textSecondary, marginTop: space.xs }}>
                            מזה מלחמה: {fmtBudget(d.warExecution)} ({warOfExecPct}%)
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ marginTop: space.md, padding: `${space.sm + 2}px ${space.md}px`, borderRadius: radius.md, background: color.surfaceMuted, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: font.small, color: color.textSecondary }}>{d.warExecution > 0 ? 'ניצול תקציב (ללא מלחמה)' : 'ניצול תקציב'}</span>
                      <span style={{ fontSize: font.heading + 1, fontWeight: weight.bold, color: execBarColor(util) }}>{util}%</span>
                    </div>
                  </Card>

                  {budgetYears.length > 1 && (
                    <Card padding={0}>
                      <div style={{ padding: `${space.lg}px ${space.lg}px ${space.sm}px` }}><CardHeader title="השוואה רב-שנתית" meta="ביצוע בפועל" style={{ marginBottom: 0 }} /></div>
                      {budgetYears.map(yr => {
                        const yd = budget.years[yr];
                        const yUtil = adjustedUtilization(yd);
                        const isActive = yr === activeYear;
                        return (
                          <button
                            key={yr}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setParam('year', yr)}
                            style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: space.md, minHeight: 44, padding: `0 ${space.lg}px`, background: isActive ? color.primarySoft : 'transparent' }}
                          >
                            <span style={{ width: 40, flexShrink: 0, fontSize: font.small, fontWeight: isActive ? weight.bold : weight.regular, color: isActive ? color.primary : color.text }}>{yr}</span>
                            <Bar value={yd.execution} max={maxExec} height={8} color={execBarColor(yUtil)} style={{ flex: 1 }} />
                            <span style={{ width: 64, flexShrink: 0, textAlign: 'left', fontSize: font.small, fontWeight: weight.medium }}>{fmtBudget(yd.execution)}</span>
                            <span style={{ width: 40, flexShrink: 0, textAlign: 'left', fontSize: font.caption, color: color.textSecondary }}>{yUtil}%</span>
                          </button>
                        );
                      })}
                      <div style={{ height: space.sm }} />
                    </Card>
                  )}

                  {grade && (
                    <Card>
                      <div style={{ display: 'flex', alignItems: 'center', gap: space.lg }}>
                        <Badge tone={GRADE_TONE[grade.letter] || 'neutral'} style={{ width: 56, height: 56, justifyContent: 'center', fontSize: 28, fontWeight: weight.bold, borderRadius: radius.lg, padding: 0 }}>{grade.letter}</Badge>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: font.body, fontWeight: weight.bold }}>ציון ביצוע תקציבי: {grade.score}/100</div>
                          <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5, marginTop: space.xxs }}>מבוסס על דיוק ביצוע, יציבות תכנון ועקביות רב-שנתית</div>
                        </div>
                      </div>
                      <div style={{ marginTop: space.lg, display: 'flex', flexDirection: 'column', gap: space.md }}>
                        {[
                          { label: 'דיוק ביצוע', val: grade.components.execution, desc: 'כמה מהתקציב המקורי בוצע בפועל' },
                          { label: 'איכות תכנון', val: grade.components.planning, desc: 'כמה התקציב השתנה מהמקורי לעל"ש' },
                          { label: 'עקביות', val: grade.components.consistency, desc: 'אחידות הביצוע לאורך השנים' },
                        ].map(c => (
                          <div key={c.label}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                              <span style={{ fontWeight: weight.medium }}>{c.label}</span>
                              <span style={{ color: color.textSecondary }}>{c.val}/100</span>
                            </div>
                            <Bar value={c.val} height={6} color={execBarColor(c.val)} />
                            <div style={{ fontSize: font.caption, color: color.textSecondary, marginTop: space.xs }}>{c.desc}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              );
            })()
        )}

        {/* ═══ QUERIES TAB ═══ */}
        {activeTab === 'queries' && (
          queries === null
            ? <SkeletonList rows={6} />
            : queries.length === 0
              ? <Card padding={0}><EmptyState icon="document" title="אין שאילתות למשרד זה" /></Card>
              : (
                <>
                  {topQuestioners.length > 0 && (
                    <Card>
                      <CardHeader title="שואלים מובילים" />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.sm }}>
                        {topQuestioners.map(t => {
                          const mk = queryMembers[t.id];
                          return (
                            <Link key={t.id} to={`/members/${t.id}`} state={{ from }} style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, minHeight: 36, padding: `2px ${space.sm}px 2px ${space.xs}px`, borderRadius: radius.pill, background: color.surfaceMuted, color: color.text, textDecoration: 'none', fontSize: font.small }}>
                              <Avatar name={mk?.name || '?'} src={mk ? photoFor(mk, 28) : undefined} size={28} />
                              {mk?.name || '…'}
                              <Badge tone="info" shape="pill" style={{ padding: '0 8px' }}>{t.count}</Badge>
                            </Link>
                          );
                        })}
                      </div>
                    </Card>
                  )}

                  <Section title="השאילתות" meta={queries.length}>
                    <Card padding={0}>
                      {queries.slice(0, queriesShown).map((q, i) => {
                        const answered = !!q.replyDate;
                        const mkName = q.memberId ? queryMembers[q.memberId]?.name : undefined;
                        return (
                          <ListRow
                            key={q.id}
                            divider={i > 0}
                            to={`/queries/${q.id}`}
                            state={{ from }}
                            title={q.name || 'שאילתה'}
                            subtitle={<span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap', marginTop: space.xxs }}><Badge tone={answered ? 'success' : 'neutral'}>{answered ? 'נענתה' : 'ממתינה'}</Badge>{[mkName, formatShortDate(q.submitDate)].filter(Boolean).join(' · ')}</span>}
                          />
                        );
                      })}
                    </Card>
                  </Section>
                  {queriesShown < queries.length && (
                    <Button fullWidth onClick={() => setParam('n', String(queriesShown + QUERIES_PAGE))}>
                      הצגת עוד ({queries.length - queriesShown} נותרו)
                    </Button>
                  )}
                </>
              )
        )}

        {/* ═══ SECONDARY LEGISLATION TAB ═══ */}
        {activeTab === 'seclaw' && (
          categoryId === null
            // meta/ministries gets each ministry's category from --ministries; until it has, the
            // items can't be matched to this ministry
            ? <Card padding={0}><EmptyState icon="document" title="הנתונים עוד לא מוכנים" body="חקיקת המשנה של המשרד תוצג כאן אחרי העדכון הבא של נתוני המשרדים." /></Card>
            : (
              <SecondaryLawList
                rows={slRows}
                error={slError}
                onRetry={() => { setSlError(false); setSlRows(null); setSlReload(n => n + 1); }}
                context="ministry"
                committeeNames={committeeNames}
                from={from}
                note={`חקיקת משנה שהמשרד הוא הגורם המתקין שלה, בכנסת ה-${slKnesset || scope.knesset}`}
                emptyTitle="אין חקיקת משנה"
                emptyBody={`לא נמצאו תקנות, צווים או דיווחים שהמשרד הוא הגורם המתקין שלהם בכנסת ה-${slKnesset || scope.knesset}.`}
              />
            )
        )}
      </div>
      </TwoPane>
      </PageContainer>
    </Layout>
  );
};

export default MinistryDetailPage;
